import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useMidiStore,
  VIRTUAL_INPUTS,
  VIRTUAL_DESTINATIONS,
  isVirtualInput,
  type MidiDevice,
} from '@/stores/midiStore'
import { useWebMidi } from '@/hooks/useWebMidi'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { useEffectiveMidi } from '@/hooks/useEffectiveMidi'
import { usePlatform } from '@/hooks/usePlatform'
import { useSequencerStore } from '@/stores/sequencerStore'
import {
  getNetworkMidiStatus,
  isNativeMidiAvailable,
  setNetworkMidiEnabled,
  showBluetoothMidiAdvertising,
  showBluetoothMidiPairing,
} from '@/lib/nativeMidi'

interface MidiRouterProps {
  /**
   * `modal` packs everything tighter for the editor's floating panel.
   * `page` adds breathing room for the dedicated `/midi` route.
   */
  variant?: 'modal' | 'page'
  /**
   * Fired when the user tries to create a route after hitting their
   * tier's `maxMidiRoutes` cap. Host pages typically open the
   * UpgradeDialog in response. When omitted the attempt is silently
   * dropped.
   */
  onRouteLimitReached?: () => void
}

/** Pointer movement (px) before a press counts as a drag instead of a click. */
const DRAG_THRESHOLD = 4

type Side = 'input' | 'output'

interface DragState {
  side: Side
  id: string
  pointerId: number
  startClientX: number
  startClientY: number
  cursor: { x: number; y: number } // container-relative
  isDragging: boolean
}

interface JackPos {
  x: number
  y: number
}

/**
 * Patchbay-style MIDI router. Inputs run down the left column,
 * outputs down the right; every active route is drawn as an SVG
 * bezier cable between them so you can read the whole routing graph
 * at a glance. Drag from any row to a row on the opposite side to
 * create a new cable. Click a cable to remove it. A plain click
 * (no drag) on an input row focuses that input in the MIDI monitor
 * below.
 */
export function MidiRouter({
  variant = 'modal',
  onRouteLimitReached,
}: MidiRouterProps) {
  const supported = useMidiStore(s => s.supported)
  const platform = usePlatform()
  const hwInputs = useMidiStore(s => s.hwInputs)
  const hwOutputs = useMidiStore(s => s.hwOutputs)
  const globalRoutes = useMidiStore(s => s.routes)
  const selectedHwInputId = useMidiStore(s => s.selectedHwInputId)
  const setSelectedHwInput = useMidiStore(s => s.setSelectedHwInput)
  const addGlobalRoute = useMidiStore(s => s.addRoute)
  const removeGlobalRoute = useMidiStore(s => s.removeRoute)

  // Layered MIDI: reads the effective route list (project override or
  // global fallback). Writes dispatch to whichever source is currently
  // active so the UI edits the thing the badge says it's editing.
  const effective = useEffectiveMidi()
  const initProjectMidiOverride = useSequencerStore(
    s => s.initProjectMidiOverride,
  )
  const clearProjectMidiOverride = useSequencerStore(
    s => s.clearProjectMidiOverride,
  )
  const setProjectMidiRoutes = useSequencerStore(s => s.setProjectMidiRoutes)

  const routes = effective.routes
  const addRoute = (inputId: string, outputId: string) => {
    if (effective.hasOverride) {
      // Dedupe: adding a route that already exists is a no-op; the
      // store-side dedupe only fires on the global action, so mirror
      // it here.
      if (routes.some(r => r.inputId === inputId && r.outputId === outputId)) {
        return
      }
      setProjectMidiRoutes([...routes, { inputId, outputId }])
    } else {
      addGlobalRoute(inputId, outputId)
    }
  }
  const removeRoute = (inputId: string, outputId: string) => {
    if (effective.hasOverride) {
      setProjectMidiRoutes(
        routes.filter(r => !(r.inputId === inputId && r.outputId === outputId)),
      )
    } else {
      removeGlobalRoute(inputId, outputId)
    }
  }

  const { features } = useEffectiveFeatures()
  // Tier cap for *creating* new routes. Existing persisted routes
  // aren't truncated on downgrade — the user just can't add more
  // until they're back under the cap.
  const maxRoutes = features?.maxMidiRoutes ?? Number.POSITIVE_INFINITY
  const atRouteLimit = routes.length >= maxRoutes

  const attemptAddRoute = useCallback(
    (inputId: string, outputId: string) => {
      // Adding a route that already exists is a no-op in the store, so
      // allow it through even at cap — it won't grow the count.
      const duplicate = routes.some(
        r => r.inputId === inputId && r.outputId === outputId,
      )
      if (!duplicate && routes.length >= maxRoutes) {
        onRouteLimitReached?.()
        return
      }
      addRoute(inputId, outputId)
    },
    [addRoute, maxRoutes, onRouteLimitReached, routes],
  )

  const { requestAccess } = useWebMidi()
  useEffect(() => {
    requestAccess()
  }, [requestAccess])

  const allInputs = useMemo<MidiDevice[]>(
    () => [...VIRTUAL_INPUTS, ...hwInputs],
    [hwInputs],
  )

  // Virtual destinations (currently just the Sequencer) sit above
  // hardware outputs in the patchbay. Cabling a hardware input → a
  // virtual destination is handled in useWebMidi: the dispatcher
  // checks `isVirtualDestination(route.outputId)` and routes the
  // data into the in-app listener instead of `access.outputs.get()`.
  const allOutputs = useMemo<MidiDevice[]>(
    () => [...VIRTUAL_DESTINATIONS, ...hwOutputs],
    [hwOutputs],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  // Tracks the visible jack circle for each row (the cable endpoint).
  // Separate from the row hit area so cables anchor to the connector
  // graphic, not the row's geometric center.
  const jackRefs = useRef<Map<string, HTMLElement | null>>(new Map())
  const [jackPositions, setJackPositions] = useState<Map<string, JackPos>>(
    new Map(),
  )
  const [drag, setDrag] = useState<DragState | null>(null)

  // Recompute jack centers whenever the device lists or routes change.
  // useLayoutEffect to ensure cables draw in the same paint as row layout.
  const recomputeJackPositions = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const containerRect = container.getBoundingClientRect()
    const next = new Map<string, JackPos>()
    jackRefs.current.forEach((el, key) => {
      if (!el) return
      const rect = el.getBoundingClientRect()
      next.set(key, {
        x: rect.left + rect.width / 2 - containerRect.left,
        y: rect.top + rect.height / 2 - containerRect.top,
      })
    })
    setJackPositions(next)
  }, [])

  useLayoutEffect(() => {
    recomputeJackPositions()
  }, [recomputeJackPositions, allInputs, allOutputs, routes, variant])

  // Layout-driven changes (window resize, container resize, modal
  // expand) need a second recompute so cables track the new sizes.
  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => recomputeJackPositions())
    ro.observe(container)
    return () => ro.disconnect()
  }, [recomputeJackPositions])

  // Pointer handlers ------------------------------------------------

  const containerPoint = useCallback((clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return { x: 0, y: 0 }
    return { x: clientX - rect.left, y: clientY - rect.top }
  }, [])

  const onRowPointerDown = useCallback(
    (side: Side, id: string) => (e: React.PointerEvent<HTMLElement>) => {
      // Ignore right/middle mouse buttons.
      if (e.button !== 0) return
      e.preventDefault()
      const point = containerPoint(e.clientX, e.clientY)
      setDrag({
        side,
        id,
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        cursor: point,
        isDragging: false,
      })
      // Capture on the row so move/up still fire if the cursor leaves.
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [containerPoint],
  )

  const onRowPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      setDrag(d => {
        if (!d || d.pointerId !== e.pointerId) return d
        const dx = e.clientX - d.startClientX
        const dy = e.clientY - d.startClientY
        const isDragging =
          d.isDragging || dx * dx + dy * dy > DRAG_THRESHOLD * DRAG_THRESHOLD
        return {
          ...d,
          cursor: containerPoint(e.clientX, e.clientY),
          isDragging,
        }
      })
    },
    [containerPoint],
  )

  const onRowPointerUp = useCallback(
    (side: Side, id: string) => (e: React.PointerEvent<HTMLElement>) => {
      const current = drag
      if (!current || current.pointerId !== e.pointerId) {
        setDrag(null)
        return
      }
      e.currentTarget.releasePointerCapture(e.pointerId)

      if (!current.isDragging) {
        // Plain click on an input row toggles monitor focus. Output
        // row clicks are no-ops to keep the model simple.
        if (side === 'input') {
          setSelectedHwInput(selectedHwInputId === id ? null : id)
        }
        setDrag(null)
        return
      }

      // Drag — find the jack the pointer was released over.
      const elem = document.elementFromPoint(e.clientX, e.clientY)
      const targetEl = elem?.closest<HTMLElement>('[data-jack-side]')
      const targetSide = targetEl?.dataset.jackSide as Side | undefined
      const targetId = targetEl?.dataset.jackId
      if (
        targetEl &&
        targetSide &&
        targetId &&
        targetSide !== current.side &&
        !(targetSide === side && targetId === id) // ignore self
      ) {
        const inputId = current.side === 'input' ? current.id : targetId
        const outputId = current.side === 'output' ? current.id : targetId
        attemptAddRoute(inputId, outputId)
      }
      setDrag(null)
    },
    [attemptAddRoute, drag, selectedHwInputId, setSelectedHwInput],
  )

  // Cancel an in-flight drag if the pointer leaves the document.
  const onRowPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (drag && drag.pointerId === e.pointerId) {
        e.currentTarget.releasePointerCapture(e.pointerId)
        setDrag(null)
      }
    },
    [drag],
  )

  // Render helpers --------------------------------------------------

  if (!supported) {
    // Three very different "no MIDI" cases — name them honestly
    // instead of blaming the browser in all of them.
    //
    // - Native (Capacitor on iOS/iPadOS/Mac Catalyst): iOS + iPadOS
    //   *do* support MIDI at the system level via CoreMIDI (USB,
    //   Bluetooth LE, Network, inter-app routing through AUM/Audiobus
    //   and friends). The `navigator.requestMIDIAccess` API just
    //   isn't exposed inside WKWebView, so our web code can't see
    //   those devices yet. Fix requires a native Capacitor plugin
    //   bridging CoreMIDI → JS (tracked as future work).
    //
    // - Desktop Safari / Firefox: Web MIDI is still gated off by
    //   default. Chrome / Edge / Opera are the reliable options.
    //
    // - Legacy browser with no Web MIDI at all (rare in 2026).
    if (platform.isNative) {
      return (
        <div className="px-4 py-3 text-sm text-content-secondary bg-surface-raised border border-brd-subtle rounded-lg space-y-1.5">
          <div className="font-semibold text-content-primary">
            MIDI isn&apos;t wired up in the native app yet.
          </div>
          <div className="text-xs leading-relaxed">
            iPad and iPhone both support MIDI (USB, Bluetooth LE, inter-app via
            AUM and similar), but Apple doesn&apos;t expose the Web MIDI API
            inside WKWebView — so Busswerks can&apos;t see your devices from
            this native build. Adding CoreMIDI access through a native plugin is
            tracked as follow-up work. In the meantime, open{' '}
            <span className="font-mono text-content-primary">busswerks.io</span>{' '}
            in Chrome/Edge on your computer to use the MIDI Router with real
            hardware.
          </div>
        </div>
      )
    }
    return (
      <div className="px-4 py-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg">
        Web MIDI isn&apos;t supported in this browser. Try Chrome, Edge, or
        Opera on desktop.
      </div>
    )
  }

  const sectionPad = variant === 'page' ? 'p-4' : 'p-3'
  const colGap = variant === 'page' ? 'gap-x-16' : 'gap-x-10'
  const rowGap = 'gap-y-2'

  const inputName = (id: string): string =>
    allInputs.find(i => i.id === id)?.name ?? 'Unknown input'
  const outputName = (id: string): string =>
    allOutputs.find(o => o.id === id)?.name ?? 'Unknown output'

  return (
    <section
      className={`bg-surface-raised border border-brd-subtle rounded-xl ${sectionPad} flex flex-col gap-3`}
    >
      <header className="flex items-center justify-between gap-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
          Patchbay
        </h3>
        <p className="text-[10px] text-content-tertiary">
          Drag from any row to the opposite side to make a cable. Click a cable
          to remove it.
        </p>
      </header>

      {/* Layered-routing banner — three states:
            · no active project → Global routing (silent, no banner)
            · active project, no override → hint + Customize button
            · active project with override → badge + Reset button       */}
      {effective.activeProjectId && !effective.hasOverride && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-brd-subtle bg-surface-sunken px-3 py-2 text-[11px] text-content-secondary">
          <span>
            <strong className="text-content-primary">
              {effective.activeProjectName}
            </strong>{' '}
            is using your global routing. Customize it for this project to keep
            the rest of your setups untouched.
          </span>
          <button
            type="button"
            onClick={() => initProjectMidiOverride()}
            className="rounded-md bg-accent-muted px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent hover:bg-[color:var(--accent-halo)]/20 transition-colors"
          >
            Customize for project
          </button>
        </div>
      )}
      {effective.activeProjectId && effective.hasOverride && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--accent-halo)] bg-accent-muted px-3 py-2 text-[11px] text-accent">
          <span>
            Editing{' '}
            <strong className="text-content-primary">
              {effective.activeProjectName}
            </strong>
            ’s project-specific routing. Global setup stays unchanged.
          </span>
          <button
            type="button"
            onClick={() => clearProjectMidiOverride()}
            className="rounded-md bg-surface-sunken px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-content-secondary hover:text-content-primary transition-colors"
          >
            Reset to global
          </button>
        </div>
      )}

      {isNativeMidiAvailable() && <WirelessMidiControls />}

      {atRouteLimit && Number.isFinite(maxRoutes) && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--accent-halo)] bg-accent-muted px-3 py-2 text-[11px] text-accent">
          <span>
            Free plan is limited to {maxRoutes}{' '}
            {maxRoutes === 1 ? 'route' : 'routes'}. Remove a cable or upgrade
            for unlimited routing.
          </span>
          {onRouteLimitReached && (
            <button
              type="button"
              onClick={onRouteLimitReached}
              className="rounded-md bg-accent px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--accent-ink)] hover:bg-[color:var(--accent-hover)] transition-colors"
            >
              Upgrade
            </button>
          )}
        </div>
      )}

      <div ref={containerRef} className="relative">
        <div className={`grid grid-cols-2 ${colGap} ${rowGap}`}>
          <ColumnHeader title="Inputs" align="left" />
          <ColumnHeader title="Outputs" align="right" />

          {allInputs.map(input => (
            <PatchRow
              key={input.id}
              side="input"
              device={input}
              isSelected={selectedHwInputId === input.id}
              isDragSource={
                drag?.side === 'input' &&
                drag.id === input.id &&
                drag.isDragging
              }
              registerJack={el => jackRefs.current.set(`input:${input.id}`, el)}
              onPointerDown={onRowPointerDown('input', input.id)}
              onPointerMove={onRowPointerMove}
              onPointerUp={onRowPointerUp('input', input.id)}
              onPointerCancel={onRowPointerCancel}
            />
          ))}
          {hwOutputs.length === 0 && (
            <div
              className="col-start-2 row-span-1 text-xs text-content-tertiary italic"
              style={{ gridRow: VIRTUAL_DESTINATIONS.length + 2 }}
            >
              No hardware MIDI outputs detected.
            </div>
          )}
          {allOutputs.map((output, idx) => (
            <PatchRow
              // Outputs sit in column 2; offset their row to stack
              // independently of the inputs column count.
              key={output.id}
              side="output"
              device={output}
              gridRow={idx + 2 /* +1 for header, +1 for 1-indexed */}
              isDragSource={
                drag?.side === 'output' &&
                drag.id === output.id &&
                drag.isDragging
              }
              registerJack={el =>
                jackRefs.current.set(`output:${output.id}`, el)
              }
              onPointerDown={onRowPointerDown('output', output.id)}
              onPointerMove={onRowPointerMove}
              onPointerUp={onRowPointerUp('output', output.id)}
              onPointerCancel={onRowPointerCancel}
            />
          ))}
        </div>

        <CableLayer
          routes={routes}
          jackPositions={jackPositions}
          drag={drag}
          onRemove={removeRoute}
          inputName={inputName}
          outputName={outputName}
        />
      </div>

      {hwInputs.length === 0 && (
        <p className="text-[11px] text-content-tertiary italic">
          No hardware MIDI inputs detected. The Virtual Keyboard above is always
          available as a source.
        </p>
      )}

      <OutputLatencyPanel />
    </section>
  )
}

/**
 * Compact per-output latency compensation panel. Rendered under
 * the patchbay so users can dial in the timing offset for any
 * routed hardware output. The sequencer's `firePad` reads these
 * values and schedules MIDI note-on that many ms earlier than
 * the corresponding in-app synth trigger — so external devices
 * with MIDI-in / sound-gen lag stay in sync with internal synth
 * sounds.
 *
 * Only shows outputs that actually have routes — avoids noise
 * from plugged-but-unused devices.
 */
function OutputLatencyPanel() {
  const hwOutputs = useMidiStore(s => s.hwOutputs)
  const routes = useMidiStore(s => s.routes)
  const outputLatencyMs = useMidiStore(s => s.outputLatencyMs)
  const setOutputLatency = useMidiStore(s => s.setOutputLatency)

  const routedOutputs = useMemo<MidiDevice[]>(() => {
    const ids = new Set(routes.map(r => r.outputId))
    return hwOutputs.filter(o => ids.has(o.id))
  }, [hwOutputs, routes])

  if (routedOutputs.length === 0) return null

  return (
    <div className="rounded-xl bg-surface-sunken border border-brd-subtle p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Output latency
        </span>
        <span className="text-[10px] font-mono text-content-tertiary">
          ms offset ahead of audio
        </span>
      </div>
      <p className="text-[11px] text-content-tertiary mb-2 leading-relaxed">
        If a hardware synth lags behind the in-app sound, bump its latency up —
        the sequencer will fire MIDI note-on that many ms earlier so the device
        catches up. Typical range: 5–30 ms. Clamped to 0–500.
      </p>
      <div className="flex flex-col gap-1.5">
        {routedOutputs.map(o => (
          <label
            key={o.id}
            className="flex items-center gap-3 text-xs font-mono"
          >
            <span className="flex-1 min-w-0 truncate text-content-primary">
              {o.name}
            </span>
            <input
              type="number"
              min={0}
              max={500}
              step={1}
              value={outputLatencyMs[o.id] ?? 0}
              onChange={e =>
                setOutputLatency(o.id, Number(e.target.value) || 0)
              }
              className="w-16 bg-surface-raised border border-brd-subtle rounded px-1 py-0.5 text-right text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
              aria-label={`Latency in ms for ${o.name}`}
            />
            <span className="text-content-tertiary">ms</span>
          </label>
        ))}
      </div>
    </div>
  )
}

interface ColumnHeaderProps {
  title: string
  align: 'left' | 'right'
}

function ColumnHeader({ title, align }: ColumnHeaderProps) {
  return (
    <div
      className={`text-[10px] uppercase tracking-wider font-semibold text-content-tertiary ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {title}
    </div>
  )
}

interface PatchRowProps {
  side: Side
  device: MidiDevice
  isSelected?: boolean
  isDragSource: boolean
  gridRow?: number
  /** Receives the visible jack circle so cables can anchor to it. */
  registerJack: (el: HTMLElement | null) => void
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void
}

function PatchRow({
  side,
  device,
  isSelected,
  isDragSource,
  gridRow,
  registerJack,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: PatchRowProps) {
  const isVirtual = isVirtualInput(device.id)
  const stateOk = isVirtual || device.state === 'connected'
  const baseStyle =
    'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs select-none cursor-grab active:cursor-grabbing transition-colors duration-150'
  const selectedStyle = isSelected
    ? 'bg-[color:var(--second-soft)] border-second text-content-primary'
    : 'bg-surface-sunken border-brd-subtle hover:border-brd-emphasis text-content-secondary'
  const dragStyle = isDragSource
    ? 'border-second ring-1 ring-[color:var(--second-soft)]'
    : ''
  const opacity = stateOk ? '' : 'opacity-60'

  return (
    <div
      data-jack-side={side}
      data-jack-id={device.id}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      className={`${baseStyle} ${selectedStyle} ${dragStyle} ${opacity}`}
      style={
        gridRow
          ? { gridColumn: 2, gridRow, justifyContent: 'flex-end' }
          : undefined
      }
    >
      {side === 'input' ? (
        <>
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              isVirtual
                ? 'bg-second'
                : stateOk
                  ? 'bg-success'
                  : 'bg-content-tertiary'
            }`}
            aria-hidden
          />
          <span className="truncate flex-1 font-medium">{device.name}</span>
          <Jack side="input" registerJack={registerJack} />
        </>
      ) : (
        <>
          <Jack side="output" registerJack={registerJack} />
          <span className="truncate flex-1 font-medium text-right">
            {device.name}
          </span>
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              stateOk ? 'bg-success' : 'bg-content-tertiary'
            }`}
            aria-hidden
          />
        </>
      )}
    </div>
  )
}

interface JackProps {
  side: Side
  registerJack: (el: HTMLElement | null) => void
}

function Jack({ side, registerJack }: JackProps) {
  return (
    <span
      ref={registerJack}
      aria-hidden
      className={`w-3 h-3 rounded-full border-2 border-second bg-surface-base flex-shrink-0 ${
        side === 'input' ? 'ml-1' : 'mr-1'
      }`}
    />
  )
}

interface CableLayerProps {
  routes: { inputId: string; outputId: string }[]
  jackPositions: Map<string, JackPos>
  drag: DragState | null
  onRemove: (inputId: string, outputId: string) => void
  inputName: (id: string) => string
  outputName: (id: string) => string
}

function CableLayer({
  routes,
  jackPositions,
  drag,
  onRemove,
  inputName,
  outputName,
}: CableLayerProps) {
  const dragPreview = useMemo(() => {
    if (!drag || !drag.isDragging) return null
    const start = jackPositions.get(`${drag.side}:${drag.id}`)
    if (!start) return null
    return cablePath(start, drag.cursor)
  }, [drag, jackPositions])

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ overflow: 'visible' }}
      aria-hidden
    >
      {routes.map(route => {
        const from = jackPositions.get(`input:${route.inputId}`)
        const to = jackPositions.get(`output:${route.outputId}`)
        if (!from || !to) return null
        const path = cablePath(from, to)
        const title = `${inputName(route.inputId)} \u2192 ${outputName(
          route.outputId,
        )} (click to remove)`
        return (
          <g
            key={`${route.inputId}::${route.outputId}`}
            className="midi-cable"
            style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
            onClick={() => onRemove(route.inputId, route.outputId)}
          >
            {/* Wider invisible hit-area so the cable is easy to click */}
            <path d={path} fill="none" stroke="transparent" strokeWidth={14} />
            <path
              d={path}
              fill="none"
              stroke="#4ECDC4"
              strokeWidth={2.5}
              strokeLinecap="round"
              opacity={0.85}
            >
              <title>{title}</title>
            </path>
          </g>
        )
      })}
      {dragPreview && (
        <path
          d={dragPreview}
          fill="none"
          stroke="#4ECDC4"
          strokeWidth={2}
          strokeLinecap="round"
          strokeDasharray="4 3"
          opacity={0.7}
        />
      )}
    </svg>
  )
}

function cablePath(from: JackPos, to: JackPos): string {
  // Bezier with horizontal handles so cables sag naturally between
  // the two columns. Handle length scales with horizontal distance
  // but never falls below 60px so short hops still curve.
  const handle = Math.max(Math.abs(to.x - from.x) * 0.5, 60)
  return `M ${from.x},${from.y} C ${from.x + handle},${from.y} ${to.x - handle},${to.y} ${to.x},${to.y}`
}

/**
 * Wireless-MIDI controls — Bluetooth LE pair/advertise + Network MIDI
 * (RTP-MIDI over Bonjour) toggle. Only rendered on native; the web
 * build has no equivalent affordance since Web MIDI doesn't expose
 * transport-specific pairing UIs. Paired/enabled endpoints then flow
 * through the same CoreMIDI → Web MIDI polyfill → MidiRouter
 * patchbay as USB and inter-app devices.
 */
function WirelessMidiControls() {
  const [networkEnabled, setNetworkEnabled] = useState(false)
  const [networkConnections, setNetworkConnections] = useState(0)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void getNetworkMidiStatus().then(status => {
      if (cancelled) return
      setNetworkEnabled(status.enabled)
      setNetworkConnections(status.connections)
    })
    // Re-poll every 5s while mounted so connection counts update when
    // peers come and go. Cheap: the native call is a few UInt32 reads.
    const interval = window.setInterval(() => {
      void getNetworkMidiStatus().then(status => {
        if (cancelled) return
        setNetworkEnabled(status.enabled)
        setNetworkConnections(status.connections)
      })
    }, 5000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [])

  const toggleNetwork = async () => {
    setBusy('network')
    setNetworkError(null)
    try {
      const res = await setNetworkMidiEnabled(!networkEnabled)
      setNetworkEnabled(res.enabled)
      if (res.error) setNetworkError(res.error)
    } finally {
      setBusy(null)
    }
  }

  const btnBase =
    'text-[11px] font-mono px-2.5 py-1 rounded-md border border-brd-subtle bg-surface-sunken text-content-secondary hover:text-content-primary hover:border-[color:var(--accent-halo)] transition-colors disabled:opacity-50'

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brd-subtle bg-surface-sunken px-3 py-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-content-tertiary mr-1">
        Wireless MIDI
      </span>
      <button
        type="button"
        className={btnBase}
        disabled={busy !== null}
        onClick={() => {
          setBusy('btPair')
          void showBluetoothMidiPairing().finally(() => setBusy(null))
        }}
        title="Scan for and pair Bluetooth LE MIDI devices"
      >
        🔗 Pair BLE device…
      </button>
      <button
        type="button"
        className={btnBase}
        disabled={busy !== null}
        onClick={() => {
          setBusy('btAdv')
          void showBluetoothMidiAdvertising().finally(() => setBusy(null))
        }}
        title="Advertise this device as a BLE MIDI peripheral"
      >
        📡 Advertise BLE
      </button>
      <button
        type="button"
        className={`${btnBase} ${
          networkEnabled ? 'border-accent text-accent hover:border-accent' : ''
        }`}
        disabled={busy === 'network'}
        onClick={() => void toggleNetwork()}
        title={
          networkEnabled
            ? 'Disable Network MIDI (RTP-MIDI) discovery'
            : 'Enable Network MIDI (RTP-MIDI) so LAN peers can connect'
        }
      >
        🌐 Network MIDI: {networkEnabled ? 'ON' : 'off'}
        {networkEnabled && networkConnections > 0 && (
          <span className="ml-1 text-accent">({networkConnections})</span>
        )}
      </button>
      {networkError && (
        <div className="basis-full text-[10px] text-destructive mt-1 leading-snug">
          {networkError}
        </div>
      )}
    </div>
  )
}
